import registryJson from "../../data/localization/detail-value-translations.json";

export type DetailValueLanguage = "en" | "zh";

export interface DetailValueTranslationEntry {
  path?: string;
  source: string;
  translations: Partial<Record<DetailValueLanguage, string>>;
}

interface DetailValueTranslationRegistry {
  version: number;
  canonicalStorage: "source";
  fallback: "source";
  description: string;
  entries: DetailValueTranslationEntry[];
}

const registry = registryJson as DetailValueTranslationRegistry;

export const DETAIL_VALUE_TRANSLATION_ENTRIES = registry.entries;

const LITERAL_PATHS = new Set(["api.platform", "timeline[].channel"]);

const LITERAL_LABEL_PATHS = new Set([
  "historicalIdentities[].source.label",
  "links[].label",
  "modelCard.label",
  "versionHistorySources[].label"
]);

const LITERAL_LEAF_PATTERN =
  /(?:^|\.)(?:aliases\[\]|name|modelId|modelIdOrAlias|officialModelIdOrAlias|productModelIdOrAlias|recordId|snapshotModelId|sourceUrl|technicalReportUrl|url)$/u;

export function normalizeDetailPath(path: string) {
  return path.replace(/^details\./u, "").replace(/\[\d+\]/gu, "[]");
}

export function literalDetailValueReason(path: string, value: string) {
  const normalizedPath = normalizeDetailPath(path);
  if (/^https?:\/\//iu.test(value)) return "url";
  if (LITERAL_PATHS.has(normalizedPath)) return "named-channel";
  if (LITERAL_LABEL_PATHS.has(normalizedPath)) return "source-title";
  if (LITERAL_LEAF_PATTERN.test(normalizedPath)) return "identifier-or-name";
  return null;
}

function translationKey(path: string | undefined, source: string) {
  return `${path ?? "*"}\u0000${source}`;
}

const TRANSLATIONS = new Map(
  DETAIL_VALUE_TRANSLATION_ENTRIES.map((entry) => [
    translationKey(
      entry.path ? normalizeDetailPath(entry.path) : undefined,
      entry.source
    ),
    entry.translations
  ])
);

export function detailValueTranslation(
  path: string,
  source: string,
  language: DetailValueLanguage
) {
  if (literalDetailValueReason(path, source)) return undefined;
  const normalizedPath = normalizeDetailPath(path);
  return (
    TRANSLATIONS.get(translationKey(normalizedPath, source))?.[language] ??
    // Only this presentation wrapper aliases a canonical value path; labels keep
    // their full path, and an exact rendered-path translation still wins.
    (normalizedPath === "dateEvidence.basis"
      ? TRANSLATIONS.get(translationKey("dateEvidence", source))?.[language]
      : undefined) ??
    TRANSLATIONS.get(translationKey(undefined, source))?.[language]
  );
}

export function localizeDetailValue(
  path: string,
  source: string,
  language: DetailValueLanguage
) {
  return detailValueTranslation(path, source, language) ?? source;
}

export function detailValueSearchTerms(
  path: string,
  source: string,
  language: DetailValueLanguage
) {
  const translated = detailValueTranslation(path, source, language);
  return translated && translated !== source ? [source, translated] : [source];
}
